// ============================================================================
// src/dashboard/LightHomePortal.tsx  -  Lane 1 CP1
// ----------------------------------------------------------------------------
// Home portal for authenticated Light-role users. Light users are field users
// contained to report/form surfaces; App's access gate enforces that boundary. This
// component is only the usable front door and shares the regular home styling.
// ============================================================================
import React from 'react';
import {useAppNavigate as useNavigate} from '../app/navigation/index.js';
import './lightPortal.css';
import {RESOURCE} from '../app/data/routeDataCatalog.js';
import type {
  LightHomeEquipmentAttentionData,
  LightHomeMissedInputsData,
  LightHomePlannerData,
} from '../app/data/routeDataCatalog.js';
import {useRouteResource} from '../app/data/useRouteResource.js';
import {addDays, fmt, toISO, todayCentralISO} from '../lib/dateUtils.js';
import {useUI} from '../contexts/UIContext.jsx';
import {useAuth} from '../contexts/AuthContext.jsx';
import {ACTION_ICON_KEYS, PLANNER_ICON_KEYS} from '../lib/plannerIcons.js';
import {buildEquipmentAttention, buildMissedDailyReports, buildMissingData, buildNext30Events} from './homeAlerts.js';
import PlannerIcon from '../components/PlannerIcon.jsx';
import Header from '../shared/Header.tsx';
import Badge from '../shared/Badge.js';
import Button from '../shared/Button.js';
import InlineNotice from '../shared/InlineNotice.js';
import {PageFrame, PageHeader} from '../shared/PageHeader.js';
import Skeleton from '../shared/Skeleton.js';

type ChevronProps = {className?: string};

type MissedDaily = {
  key: string;
  iconKey: string;
  label?: string | null;
  type: string;
  date: string;
};

type EquipmentAttentionItem = {
  key: string;
  kind: string;
  type: string;
  typeLabel: string;
  detail: string;
};

type EquipmentAttention = {
  key: string;
  kind: string;
  label?: string | null;
  slug: string | null | undefined;
  detail: string;
  pill?: string;
  items?: EquipmentAttentionItem[];
};

type NextEvent = {
  type: string;
  date: string;
  label: string;
  iconKey: string;
  subline?: string;
  reminder?: boolean;
  color?: string;
};

function Chevron({className}: ChevronProps) {
  return (
    <svg
      className={className}
      width="18"
      height="18"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2.2"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
    >
      <path d="M9 6l6 6-6 6" />
    </svg>
  );
}

function WrenchGlyph() {
  return (
    <svg
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
    >
      <path d="M14.7 6.3a1 1 0 0 0 0 1.4l1.6 1.6a1 1 0 0 0 1.4 0l3.77-3.77a6 6 0 0 1-7.94 7.94l-6.91 6.91a2.12 2.12 0 0 1-3-3l6.91-6.91a6 6 0 0 1 7.94-7.94l-3.76 3.76z" />
    </svg>
  );
}

function missedDailyCutoff(): string {
  return toISO(addDays(new Date(), -7));
}

function resourceErrorMessage(error: unknown): string {
  return error instanceof Error ? error.message : String(error || 'unknown error');
}

export default function LightHomePortal() {
  const {setView} = useUI();
  const {authState} = useAuth();
  const navigate = useNavigate();
  const name = authState && typeof authState === 'object' ? authState.name || '' : '';
  const lightHomeEquipmentAttention = useRouteResource(RESOURCE.HOME_LIGHT_EQUIPMENT_ATTENTION);
  const lightHomePlanner = useRouteResource(RESOURCE.HOME_LIGHT_PLANNER);
  const lightHomeMissedInputs = useRouteResource(RESOURCE.HOME_LIGHT_MISSED_INPUTS, {cutoff: missedDailyCutoff()});
  // Review F4: the missed card caps at 8 rows and expands on demand.
  const [missedShown, setMissedShown] = React.useState(8);
  const lightHomeEquipmentData = lightHomeEquipmentAttention.data as LightHomeEquipmentAttentionData | null;
  const lightHomePlannerData = lightHomePlanner.data as LightHomePlannerData | null;
  const lightHomeMissedInputsData = lightHomeMissedInputs.data as LightHomeMissedInputsData | null;

  // The planner is the portal's core data boundary. Unlike the historic
  // equipment and missed-report slices below, a failed planner read must never
  // render an empty-looking farm: that would hide real active targets and make
  // a transport failure indistinguishable from a healthy empty planner.
  if (lightHomePlanner.loading || (!lightHomePlannerData && !lightHomePlanner.error)) {
    return (
      <div data-light-portal="1" className="light-portal">
        {React.createElement(Header)}
        <main>
          <PageFrame>
            <PageHeader title="Field Portal" />
            {/* F05: a skeleton while the planner loads, no spinner. */}
            <div data-light-home-planner-loading="1" aria-live="polite">
              <Skeleton lines={4} label="Loading your planner data" />
            </div>
          </PageFrame>
        </main>
      </div>
    );
  }

  if (lightHomePlanner.error || !lightHomePlannerData) {
    return (
      <div data-light-portal="1" className="light-portal">
        {React.createElement(Header)}
        <main>
          <PageFrame>
            <PageHeader title="Field Portal" />
            {/* F04: the error banner carries its own Retry. */}
            <div data-light-home-planner-error="1">
              <InlineNotice
                notice={{
                  kind: 'error',
                  message: `Could not load Field Portal planner. Please retry. (${resourceErrorMessage(lightHomePlanner.error)})`,
                }}
                action={
                  <Button
                    variant="secondary"
                    data-light-home-planner-retry="1"
                    onClick={() => void lightHomePlanner.refetch()}
                  >
                    Retry
                  </Button>
                }
              />
            </div>
          </PageFrame>
        </main>
      </div>
    );
  }

  const batches = lightHomePlannerData.batches;
  const breedingCycles = lightHomePlannerData.breedingCycles;
  const farrowingRecs = lightHomePlannerData.farrowingRecs;
  const feederGroups = lightHomePlannerData.feederGroups;
  const breeders = lightHomePlannerData.breeders;
  const layerGroups = lightHomePlannerData.layerGroups;
  const missedCleared = lightHomePlannerData.missedCleared;
  const broilerDailys = lightHomeMissedInputsData?.broilerDailys || [];
  const pigDailys = lightHomeMissedInputsData?.pigDailys || [];
  const layerDailysRecent = lightHomeMissedInputsData?.layerDailysRecent || [];
  const cattleDailysRecent = lightHomeMissedInputsData?.cattleDailysRecent || [];
  const sheepDailysRecent = lightHomeMissedInputsData?.sheepDailysRecent || [];
  const cattleForHome = lightHomeMissedInputsData?.cattleForHome || [];
  const sheepForHome = lightHomeMissedInputsData?.sheepForHome || [];
  // This is an optional attention block. Its pre-resource reads ignored their
  // failures independently, so an absent payload intentionally paints no
  // attention rows and never gates the Field Portal itself.
  const equipment = lightHomeEquipmentData?.equipment || [];
  const equipmentCompletions = lightHomeEquipmentData?.equipmentCompletions || {};
  const equipmentFuelings = lightHomeEquipmentData?.equipmentFuelings || {};

  const allMissed: MissedDaily[] = buildMissedDailyReports({
    batches,
    broilerDailys,
    pigDailys,
    layerDailysRecent,
    cattleDailysRecent,
    sheepDailysRecent,
    feederGroups,
    breeders,
    layerGroups,
    cattleForHome,
    sheepForHome,
    missedCleared,
  } as Parameters<typeof buildMissedDailyReports>[0]);
  // Until the daily reads land, every active target would read as missed, so
  // the section shows a loading line instead of a count (Ronnie's note 6), and
  // a failed read says so with Retry instead of listing reports that may exist.
  const missedReportsLoading =
    lightHomeMissedInputs.loading || (!lightHomeMissedInputsData && !lightHomeMissedInputs.error);
  // Each slice resolves to [] on its own failure and is named in sliceErrors;
  // every slice here is a missed-report input.
  const missedReportsUnavailable =
    !missedReportsLoading &&
    (Boolean(lightHomeMissedInputs.error) || (lightHomeMissedInputsData?.sliceErrors || []).length > 0);
  // Missing data (Ronnie's note 2): same rows as the full Home, read-only here
  // because Light cannot open batch records. Planner-only input.
  const missingData = buildMissingData({batches, today: todayCentralISO()} as Parameters<typeof buildMissingData>[0]);
  const equipmentAttention: EquipmentAttention[] = buildEquipmentAttention({
    equipment,
    equipmentFuelings,
    equipmentCompletions,
    missedCleared,
  } as Parameters<typeof buildEquipmentAttention>[0]);
  const weekEvents: NextEvent[] = buildNext30Events({
    batches,
    breedingCycles,
    farrowingRecs,
    feederGroups,
  } as Parameters<typeof buildNext30Events>[0]);

  const tiles = [
    {
      label: 'Enter Daily Reports',
      desc: 'Broiler, layer, pig, cattle, sheep, eggs',
      view: 'webformhub',
      iconKey: ACTION_ICON_KEYS.dailyReports,
    },
    {
      label: 'Pasture Map',
      desc: 'View fields and animal locations',
      view: 'pastureMap',
      iconKey: PLANNER_ICON_KEYS.pastureMap,
    },
    {
      label: 'Add Feed',
      desc: 'Quick feed log',
      view: 'addfeed',
      iconKey: ACTION_ICON_KEYS.feed,
    },
    {
      label: 'Equipment',
      desc: 'Fueling and checklists',
      view: 'fuelingHub',
      iconKey: PLANNER_ICON_KEYS.tractor,
    },
    {
      label: 'Tasks',
      desc: 'Your tasks',
      view: 'tasks',
      iconKey: ACTION_ICON_KEYS.tasks,
    },
    {
      label: 'View Past Reports',
      desc: 'Daily report logs and equipment',
      view: 'mySubmissions',
      iconKey: ACTION_ICON_KEYS.pastReports,
    },
  ];

  function openEquipment(slug: string | null | undefined) {
    if (slug) navigate('/equipment/' + slug);
  }

  return (
    // C17-A: the Light portal follows the constitution; only Home is the island.
    <div data-light-portal="1" className="light-portal">
      {React.createElement(Header)}
      <main>
        <PageFrame>
          <div data-light-home-intro="1">
            <PageHeader
              title="Field Portal"
              summary={`${name ? `Signed in as ${name}` : 'Signed in'} · choose what you need`}
            />
          </div>

          <section className="tiles" data-light-portal-grid="1">
            {tiles.map((t) => (
              <button
                key={t.view}
                type="button"
                data-light-portal-tile={t.view}
                className="tile wcf-clickable"
                onClick={() => setView(t.view)}
              >
                <span className="coin">
                  <PlannerIcon iconKey={t.iconKey} size={34} />
                </span>
                <span className="tile-text">
                  <span className="tile-label">{t.label}</span>
                  <span className="tile-sub">{t.desc}</span>
                </span>
                <Chevron className="tile-go" />
              </button>
            ))}
          </section>

          <div data-light-home-alerts="1" style={{display: 'flex', flexDirection: 'column', gap: 18}}>
            {/* Missing Data sits above Missed Daily Reports (note 2). This alert
              stack shows only sections that need attention, so an empty one is
              left out here. */}
            {missingData.length > 0 && (
              <section className="block" data-light-home-missing-data="1">
                <div className="block-head">
                  <h2 className="section-label">Missing Data</h2>
                  <Badge variant="warn">{missingData.length}</Badge>
                </div>
                <ul className="panel">
                  {missingData.map((m) => (
                    <li key={m.key} className="litem" data-light-home-missing-data-row={m.key}>
                      <span className="coin coin-sm">
                        <PlannerIcon iconKey={m.iconKey} size={22} />
                      </span>
                      <div className="litem-body">
                        <div className="litem-title">{m.batchName}</div>
                        <div className="litem-meta">{m.detail}</div>
                      </div>
                    </li>
                  ))}
                </ul>
              </section>
            )}

            {missedReportsLoading && (
              <section className="block" data-light-home-missed-dailys-state="loading">
                <div className="block-head">
                  <h2 className="section-label">Missed Daily Reports</h2>
                </div>
                <div aria-live="polite">
                  <Skeleton lines={3} label="Loading daily reports…" />
                </div>
              </section>
            )}
            {missedReportsUnavailable && (
              <section className="block" data-light-home-missed-dailys-state="unavailable">
                <div className="block-head">
                  <h2 className="section-label">Missed Daily Reports</h2>
                </div>
                <InlineNotice
                  notice={{
                    kind: 'error',
                    message: "Daily reports could not be loaded, so missed reports can't be checked right now.",
                  }}
                  action={
                    <Button variant="secondary" onClick={() => void lightHomeMissedInputs.refetch()}>
                      Retry
                    </Button>
                  }
                />
              </section>
            )}
            {!missedReportsLoading && !missedReportsUnavailable && allMissed.length > 0 && (
              <section className="block" data-light-home-missed-dailys="1">
                <div className="block-head">
                  <h2 className="section-label label-danger">Missed Daily Reports</h2>
                  <Badge variant="danger">{allMissed.length}</Badge>
                </div>
                <ul className="panel">
                  {allMissed.slice(0, missedShown).map((m) => (
                    <li key={m.key} className="litem" data-light-home-missed-daily-row={m.key}>
                      <span className="coin coin-sm">
                        <PlannerIcon iconKey={m.iconKey} size={22} />
                      </span>
                      <div className="litem-body">
                        <div className="litem-title">{m.label}</div>
                        <div className="litem-meta">
                          {m.type} · No daily report for {fmt(m.date)}
                        </div>
                      </div>
                    </li>
                  ))}
                </ul>
                {/* Review F4: the card caps at 8 rows and expands on demand. */}
                {allMissed.length > missedShown && (
                  <div style={{marginTop: 8}}>
                    <Button
                      variant="secondary"
                      onClick={() => setMissedShown((n: number) => n + 8)}
                      data-light-home-missed-show-more="1"
                    >
                      Show 8 More ({allMissed.length - missedShown} Hidden)
                    </Button>
                  </div>
                )}
              </section>
            )}

            {equipmentAttention.length > 0 && (
              <section className="block" data-light-home-equipment-attention="1">
                <div className="block-head">
                  <span className="head-ic">
                    <WrenchGlyph />
                  </span>
                  <h2 className="section-label">Equipment Attention</h2>
                  {/* Q13: the count takes the tone of the worst item (L4). */}
                  <Badge
                    variant={
                      equipmentAttention.some((a) => a.kind === 'overdue')
                        ? 'danger'
                        : equipmentAttention.some((a) => a.kind !== 'warranty')
                          ? 'warn'
                          : 'info'
                    }
                  >
                    {equipmentAttention.length}
                  </Badge>
                </div>
                <ul className="panel">
                  {equipmentAttention.map((a) => {
                    const led = a.kind === 'overdue' ? 'led-danger' : 'led-warn';
                    const badge: 'danger' | 'info' | 'warn' =
                      a.kind === 'overdue' ? 'danger' : a.kind === 'warranty' ? 'info' : 'warn';
                    const badgeText =
                      a.pill || (a.kind === 'overdue' ? 'Overdue' : a.kind === 'warranty' ? 'Warranty' : 'Skipped');
                    const items = Array.isArray(a.items) ? a.items : [];
                    return (
                      <li
                        key={a.key}
                        className="litem eq is-link"
                        data-attention-kind={a.kind}
                        data-equipment-slug={a.slug}
                        role="button"
                        tabIndex={0}
                        onClick={() => openEquipment(a.slug)}
                        onKeyDown={(e) => {
                          if (e.key === 'Enter' || e.key === ' ') {
                            e.preventDefault();
                            openEquipment(a.slug);
                          }
                        }}
                      >
                        <span className={'eq-led ' + led} />
                        <div className="litem-body">
                          <div className="litem-title">{a.label}</div>
                          {items.length > 0 ? (
                            <div className="litem-types">
                              {items.map((it) => (
                                <div className="litem-type-row" key={it.key} data-attention-item-kind={it.kind}>
                                  <span className={'type-chip type-' + it.type}>{it.typeLabel}</span>
                                  <span className="litem-meta">{it.detail}</span>
                                </div>
                              ))}
                            </div>
                          ) : (
                            <div className="litem-meta">{a.detail}</div>
                          )}
                        </div>
                        <Badge variant={badge}>{badgeText}</Badge>
                        <span className="chev" aria-hidden="true">
                          ›
                        </span>
                      </li>
                    );
                  })}
                </ul>
              </section>
            )}
          </div>

          {weekEvents.length > 0 && (
            <section className="block" data-light-home-next-30="1">
              <div className="block-head">
                <h2 className="section-label">Next 30 Days</h2>
                <Badge variant="neutral">{weekEvents.length}</Badge>
              </div>
              <ul className="panel">
                {weekEvents.map((e, i) => (
                  <li
                    key={`${e.type}-${e.date}-${e.label}-${i}`}
                    className="litem"
                    data-light-home-next-30-row={e.type}
                  >
                    <span className="coin coin-sm">
                      <PlannerIcon iconKey={e.iconKey} size={18} />
                    </span>
                    <div className="litem-body">
                      <div className="litem-title">{e.label}</div>
                      <div className="litem-meta">{e.subline || fmt(e.date)}</div>
                    </div>
                    {!e.reminder && <span className="dot" style={{background: e.color}} />}
                  </li>
                ))}
              </ul>
            </section>
          )}
        </PageFrame>
      </main>
    </div>
  );
}
